import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
} from '@angular/forms';
import {
  Document,
  DocumentType,
  EntityType,
  Partner,
  PartnerState,
  PartnerType,
  TableColumn,
  ToastConfig
} from '@app/shared';
import { NZ_DRAWER_DATA, NzDrawerRef } from 'ng-zorro-antd/drawer';
import {
  customUpload,
  DocumentService,
  DrawerEscapeBlockerService,
  getBase64,
  GlobalValuesService,
  getPartnerOptions,
  getPartnerDisplayName,
  PartnerService,
  removeEmptyFormDataFields
} from '@app/core';
import { catchError, firstValueFrom, Observable, tap, throwError } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NzUploadChangeParam, NzUploadFile } from 'ng-zorro-antd/upload';
import { NzNotificationPlacement, NzNotificationService } from 'ng-zorro-antd/notification';
import { HttpEvent, HttpEventType } from '@angular/common/http';
import { MaestrosUtilsService } from '../../services/maestros-utils.service';

const REQUIRED_ERROR_MESSAGE = 'Este campo es requerido';
const EMAIL_FORMAT_ERROR_MESSAGE = 'Introduce un correo electrónico válido';

export function getEmailValidationMessage(
  control: AbstractControl | null,
): string {
  if (control?.hasError('required')) {
    return REQUIRED_ERROR_MESSAGE;
  }

  if (control?.hasError('pattern')) {
    return EMAIL_FORMAT_ERROR_MESSAGE;
  }

  return '';
}

@Component({
  selector: 'app-modify-partner-drawer',
  standalone: false,
  templateUrl: './modify-partner-drawer.component.html',
  styleUrl: './modify-partner-drawer.component.less',
})
export class ModifyPartnerDrawerComponent implements OnInit, OnDestroy {
  getEmailValidationMessage = getEmailValidationMessage;

  nzData: {
    columnsData: TableColumn[];
    editingElement: Partner | null;
    maestroName: string;
  } = inject(NZ_DRAWER_DATA);
  private destroyRef = inject(DestroyRef);

  isUpdating = false;
  step: 1 | 2 | 3 | 4 = 1;

  partnerForm!: FormGroup;
  partnerTypesList: PartnerType[] = [];
  statesList: PartnerState[] = [];
  countriesList: any[] = [];
  currenciesList: any[] = [];
  relatedCompaniesList: Partner[] = [];
  documentTypesList: DocumentType[] = [];
  documentsColumn: TableColumn | undefined;

  removeEmptyFormDataFields = removeEmptyFormDataFields;

  toastConfig: ToastConfig = {
    icon: 'check_circle',
    iconColor: '#6CDA75',
    content: this.nzData.editingElement
      ? 'Contacto actualizado'
      : 'Contacto añadido',
    showUndo: false,
  };

  notRef: any;
  @ViewChild('notificationTmpl') notificationTmpl: any;
  uploadedDocuments: any[] = [];
  savingPartner = false;
  avatarUrl: any;

  @Output() createEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output() updateEvent: EventEmitter<any> = new EventEmitter<any>();

  formIsFinish: boolean = false;

  customUpload = customUpload;

  constructor(
    private drawerRef: NzDrawerRef<string>,
    private fb: FormBuilder,
    private ps: PartnerService,
    private notificationService: NzNotificationService,
    private cdRef: ChangeDetectorRef,
    private documentService: DocumentService,
    private globalValuesService: GlobalValuesService,
    private ms: MaestrosUtilsService,
    private drawerEscapeBlocker: DrawerEscapeBlockerService,
  ) { }

  ngOnInit(): void {
    this.ms.loading = true;
    this.setForm();
    this.loadDocumentTypes();

    // Set title appropriately
    this.toastConfig.content = this.nzData.editingElement
      ? 'Contacto actualizado'
      : 'Contacto añadido';

    // If partner exists, this is an update
    this.isUpdating = !!this.nzData.editingElement;

    // Get the documents column
    this.documentsColumn = this.nzData.columnsData.find(
      (col) => col.pathData?.name === 'documents',
    );

    // If we're editing, load the partner's documents
    if (this.isUpdating && this.nzData.editingElement?.id) {
      this.loadPartnerDocuments(this.nzData.editingElement.id);
    }

    // Setup the listener for isCompany changes
    this.setupIsCompanyListener();
    this.ms.loading = false;

    // Registrar handler nativo en fase de captura para interceptar Escape
    this.drawerEscapeBlocker.block();
  }

  loadDocumentTypes(): void {
    this.globalValuesService.documentTypes$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((documentTypes) => {
        this.documentTypesList = documentTypes;
      });
  }

  // Metodo para crear el formulario dinamico
  setForm(): void {
    const formGroupConfig: { [key: string]: any } = {};

    // Se genera la configuracion general del formulario por cada propiedad que se tenga
    this.nzData.columnsData.forEach((column) => {
      if (
        this.nzData.editingElement ||
        (!this.nzData.editingElement && column.pathData!.name !== 'id')
      ) {
        if (column.pathData!.type === 'select' && !column.optionList?.length) {
          //this.missingData = column.pathData!.name;
          return;
        }

        if (column.pathData!.type === 'multi-select') {
          formGroupConfig[column.pathData!.name] = [];
        } else {
          formGroupConfig[column.pathData!.name] = [
            column.defaultValue ? column.defaultValue : '',
          ];
        }
        if (column.validators && column.validators.length > 0) {
          formGroupConfig[column.pathData!.name].push(
            column.validators.map((validator) => validator.function),
          );
        }
      }
    });

    // Ensure we have a documents control as a FormArray
    formGroupConfig['documents'] = this.fb.array([]);

    formGroupConfig['isBlacklisted'] = false;

    // Se crea el formulario con la configuracion anterior
    this.partnerForm = this.fb.group(formGroupConfig);

    // Si se esta editando se llena el form con los datos actuales
    if (this.nzData.editingElement) {
      this.partnerForm.patchValue({
        ...this.nzData.editingElement,
        name: getPartnerDisplayName(this.nzData.editingElement),
      });
      if (this.nzData.editingElement.avatar) {
        this.avatarUrl = this.nzData.editingElement.avatar;
      }
      if (this.nzData.editingElement.employee) {
        const employeeControl = this.partnerForm.get('employee');
        employeeControl?.setValue(this.nzData.editingElement.employee);
      }
      // Aplicar estado inicial del campo de divisa basado en isCompany
      const isCompany = this.partnerForm.get('isCompany')?.value;
      const currencyIdControl = this.partnerForm.get('currencyId');
      if (!isCompany && currencyIdControl) {
        currencyIdControl.disable();
      }
    } else {
      // Si es nuevo contacto, deshabilitar divisa por defecto (solo empresas pueden tener divisa)
      const currencyIdControl = this.partnerForm.get('currencyId');
      if (currencyIdControl) {
        currencyIdControl.disable();
      }
    }

    this.formIsFinish = true;
  }

  close(): void {
    this.drawerRef.close();
  }

  nextStep(): void {
    if (this.stepValid(this.step)) {
      this.step += 1 as any;
    }
  }

  previousStep(): void {
    this.step -= 1 as any;
  }

  stepValid(stepNumber: number): boolean {
    switch (stepNumber) {
      case 1:
        return this.partnerForm.get('name')?.valid || false;
      default:
        return true;
    }
  }

  addDocument(file: File): void {
    const defaultExpiration = new Date();
    defaultExpiration.setMonth(defaultExpiration.getMonth() + 1);
    const document = {
      file: file,
      name: file.name,
      size: this.formatFileSize(file.size),
      documentTypeId: null,
      type: null,
      progress: 0,
      index: this.uploadedDocuments.length, // Track index for form array mapping
      isExisting: false, // Mark as a new document
      expirationDate: defaultExpiration,
      id: null, // No document ID yet
    };
    this.uploadedDocuments.push(document);

    // Add a new FormGroup to the documents FormArray
    this.getDocumentsFormArray().push(
      this.fb.group({
        documentTypeId: [null],
        name: [document.name],
        file: [document.file],
        expirationDate: [document.expirationDate],
      }),
    );
  }

  async removeDocument(index: number): Promise<void> {
    this.savingPartner = true;
    const document = this.uploadedDocuments[index];

    // If this is an existing document, we need to unlink and delete it
    if (document.isExisting && document.id) {
      try {
        // First unlink the document from the partner
        if (this.nzData.editingElement?.id) {
          await this.unlinkDocument(document.id, this.nzData.editingElement.id);
        }

        // Then delete the document itself
        await this.deleteDocument(document);
      } catch (error) {
        console.error('Error removing document:', error);
      }
    }

    // Remove from our arrays
    this.uploadedDocuments.splice(index, 1);

    // Update indices after removal
    this.uploadedDocuments.forEach((doc, i) => (doc.index = i));

    // Remove from FormArray
    this.getDocumentsFormArray().removeAt(index);
  }

  formatFileSize(size: number): string {
    if (size < 1024) {
      return size + ' B';
    } else if (size < 1024 * 1024) {
      return (size / 1024).toFixed(2) + ' KB';
    } else {
      return (size / (1024 * 1024)).toFixed(2) + ' MB';
    }
  }

  onDocumentTypeChange(documentTypeId: any, document: any): void {
    // Convert to number if it's not already (handles different event types)
    const typeId =
      typeof documentTypeId === 'number'
        ? documentTypeId
        : Number(documentTypeId);

    // Update the document object
    document.documentTypeId = typeId;
    const documentType = this.documentTypesList.find((dt) => dt.id === typeId);
    if (documentType) {
      document.type = documentType.name;
    }

    // The form control is already updated by the two-way binding
  }

  /**
   * Checks if all uploaded documents have a valid document type assigned
   * @returns boolean indicating if all documents have valid types
   */
  allDocumentsHaveValidTypes(): boolean {
    // If there are no documents, return true (no validation needed)
    if (this.uploadedDocuments.length === 0) {
      return true;
    }

    // Check if any document is missing a documentTypeId
    const invalidDocuments = this.uploadedDocuments.filter(
      (doc) => !doc.documentTypeId,
    );

    // Return true if all documents have valid types (no invalid documents)
    return invalidDocuments.length === 0;
  }

  onFileSelected(event: any): void {
    const files = event.target.files;
    if (files && files.length > 0) {
      for (let i = 0; i < files.length; i++) {
        this.addDocument(files[i]);
      }
    }
  }

  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (event.dataTransfer && event.dataTransfer.files) {
      const files = event.dataTransfer.files;
      for (let i = 0; i < files.length; i++) {
        this.addDocument(files[i]);
      }
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  // Helper method to get the documents FormArray
  getDocumentsFormArray(): FormArray {
    return this.partnerForm.get('documents') as FormArray;
  }

  // Helper method to get a form control for a specific document property
  getDocumentFormControl(
    documentIndex: number,
    controlName: string,
  ): FormControl {
    const documentsArray = this.getDocumentsFormArray();
    if (documentsArray.length > documentIndex) {
      const docFormGroup = documentsArray.at(documentIndex) as FormGroup;
      return docFormGroup.get(controlName) as FormControl;
    }
    return new FormControl(null);
  }

  preparePartnerData(): FormData {
    const formValue = this.partnerForm.value;

    // Función auxiliar para agregar solo valores no nulos
    const appendIfNotNull = (key: string, value: any) => {
      if (value !== null && value !== undefined) {
        formData.append(key, value);
      }
    };

    const formData = new FormData();
    const displayName = (formValue.name ?? '').trim();
    appendIfNotNull('name', displayName);
    appendIfNotNull('partnerTypeId', formValue.partnerTypeId);
    appendIfNotNull('vat', formValue.vat);
    appendIfNotNull('imo', formValue.imo ? 'true' : 'false');
    appendIfNotNull('email', formValue.email);
    appendIfNotNull('billingEmail', formValue.billingEmail);
    appendIfNotNull('phone', formValue.phone);
    appendIfNotNull('mobile', formValue.mobile);
    appendIfNotNull('website', formValue.website);
    appendIfNotNull('street', formValue.street);
    appendIfNotNull('street2', formValue.street2);
    appendIfNotNull('city', formValue.city);
    appendIfNotNull('stateId', formValue.stateId);
    appendIfNotNull('zip', formValue.zip);
    appendIfNotNull('countryId', formValue.countryId);
    appendIfNotNull('parentId', formValue.parentId);
    appendIfNotNull('comment', formValue.comment);
    appendIfNotNull('latitude', formValue.latitude);
    appendIfNotNull('longitude', formValue.longitude);
    appendIfNotNull('currencyId', formValue.currencyId);
    appendIfNotNull('isActive', formValue.isActive ? 'true' : 'false');
    appendIfNotNull('isBlacklisted', 'false');

    if (formValue.avatar?.name) {
      formData.append('avatarFile', formValue.avatar, formValue.avatar.name);
    }
    if (this.nzData.editingElement?.id) {
      appendIfNotNull('id', this.nzData.editingElement?.id.toString());
    }
    appendIfNotNull('isCompany', formValue.isCompany ? 'true' : 'false');
    if (!formValue.isCompany) {
      appendIfNotNull('surName', '');
      appendIfNotNull('employee', formValue.employee ? 'true' : 'false');
      appendIfNotNull('position', formValue.position);
    } else {
      appendIfNotNull('vat', formValue.vat);
    }
    return formData;
  }

  async savePartner(close: boolean): Promise<void> {
    if (this.partnerForm.valid) {
      this.savingPartner = true;
      try {
        const partnerData = this.preparePartnerData();
        const response = await this.updateOrCreatePartner(partnerData);

        if (response && response.success) {
          // If we have documents to upload, process them after partner is saved
          if (
            this.uploadedDocuments.length > 0 &&
            response.data &&
            response.data.id
          ) {
            await this.uploadPartnerDocuments(response.data.id);
          }

          // Manually populate parent if missing (to avoid refetching)
          if (response.data.parentId && !response.data.parent) {
            const parentColumn = this.nzData.columnsData.find(
              (c) => c.pathData?.name === 'parentId',
            );
            if (parentColumn && parentColumn.optionList) {
              const parent = parentColumn.optionList.find(
                (p: any) => p.id === response.data.parentId,
              );
              if (parent) {
                response.data.parent = parent;
              }
            }
          }
          if (this.isUpdating) {
            this.updateEvent.emit(response.data);
            this.close();
            return;
          }
          this.createEvent.emit(response.data);
        }

        if (close) {
          this.close();
        } else {
          this.setForm();
          this.uploadedDocuments = [];
          this.savingPartner = false;
        }
      } catch (error) {
        console.error('Error saving partner:', error);
        this.savingPartner = false;
      }
    }
  }

  async uploadPartnerDocuments(partnerId: number): Promise<void> {
    // Get documents that have a documentTypeId from the form array
    const documentsArray = this.getDocumentsFormArray();
    const uploadPromises = this.uploadedDocuments
      .filter((doc) => doc.documentTypeId && doc.file) // Only upload documents with a selected type and file
      .map((doc) => this.uploadDocument(doc, partnerId));

    try {
      await Promise.all(uploadPromises);
    } catch (error) {
      console.error('Error uploading documents:', error);
    }
  }

  async uploadDocument(document: any, partnerId: number): Promise<void> {
    if (!document.file || !document.documentTypeId) {
      return;
    }

    // Prepare the FormData object for the document upload
    const formData = new FormData();
    formData.append('file', document.file);
    formData.append('name', document.name);
    formData.append('partnerId', partnerId.toString());
    formData.append('ownerId', partnerId.toString());
    formData.append('documentTypeId', document.documentTypeId.toString());
    formData.append(
      'expirationDate',
      document.expirationDate.toISOString?.()
        ? document.expirationDate.toISOString()
        : document.expirationDate,
    );
    formData.append('type', document.type);

    try {
      await new Promise<void>((resolve, reject) => {
        this.documentService.createDocument(formData).subscribe({
          next: (event: HttpEvent<any>) => {
            if (event.type === HttpEventType.UploadProgress && event.total) {
              document.progress = Math.round(
                (100 * event.loaded) / event.total,
              );
              this.cdRef.detectChanges();
            } else if (event.type === HttpEventType.Response) {
              document.progress = 100;
              this.cdRef.detectChanges();

              // After successful upload, create document link
              if (event.body && event.body.data && event.body.data.id) {
                const documentId = event.body.data.id;
                this.createDocumentLink(documentId, partnerId).subscribe({
                  next: (response: any) => {
                    this.emitUpdateEvent(response.data);
                  },
                  error: (e) => {
                    console.error(e);
                  },
                });
              }

              resolve();
            }
          },
          error: (error) => {
            console.error('Error uploading document:', error);
            reject(error);
          },
          complete: () => {
            document.progress = 100;
            this.cdRef.detectChanges();
          },
        });
      });
    } catch (error) {
      console.error('Error in document upload:', error);
    }
  }

  /**
   * Creates or updates a document link after upload or when changing status
   * @param documentId The ID of the uploaded document
   * @param partnerId The ID of the partner to link to
   * @param isLinked Whether to link (true) or unlink (false) the document
   */
  createDocumentLink(
    documentId: number,
    partnerId: number,
    isLinked: boolean = true,
  ): Observable<any> {
    const documentLink = {
      documentId: documentId,
      innerLinkDocumentPayload: {
        entityId: partnerId,
        isLinked: isLinked,
      },
      entityType: 'Partner' as EntityType,
    };

    return this.documentService.createDocumentLink(documentLink).pipe(
      tap((response) => {
        console.log(
          `Document ${isLinked ? 'link' : 'unlink'} successful`,
          response,
        );
      }),
      catchError((error) => {
        console.error(
          `Error ${isLinked ? 'linking' : 'unlinking'} document:`,
          error,
        );
        return throwError(() => error);
      }),
    );
  }

  inspectFormData(partnerData: FormData) {
    console.log('=== FormData Contents ===');

    // Recorrer todas las entradas
    for (const [key, value] of partnerData.entries()) {
      if (value instanceof File) {
        console.log(
          `${key}: File (${value.name}, ${value.size} bytes, ${value.type})`,
        );
      } else {
        console.log(`${key}: ${value}`);
      }
    }
    console.log('=========================');
  }

  async updateOrCreatePartner(partnerData: FormData): Promise<any> {
    this.ms.loading = true;
    const isUpdate = !!this.nzData.editingElement?.id;

    if (partnerData.has('countryId') && partnerData.get('countryId') === '0') {
      partnerData.delete('countryId');
    }
    if (
      partnerData.has('currencyId') &&
      partnerData.get('currencyId') === '0'
    ) {
      partnerData.delete('currencyId');
    }

    try {
      if (isUpdate) {
        return await firstValueFrom(
          this.ps.updatePartner(partnerData).pipe(
            tap(() => this.handleSuccess(isUpdate)),
            catchError((error) => this.handleError(error, isUpdate)),
          ),
        );
      } else {
        const createData = removeEmptyFormDataFields(partnerData);
        return await firstValueFrom(
          this.ps.createPartner(createData).pipe(
            tap(() => this.handleSuccess(isUpdate)),
            catchError((error) => this.handleError(error, isUpdate)),
          ),
        );
      }
    } catch (e) {
      // Los errores ya están manejados en handleError
      console.error(e);
    }
  }

  private handleSuccess(isUpdate: boolean) {
    this.toastConfig.msg = isUpdate
      ? 'Contacto actualizado.'
      : 'Contacto creado.';
    this.toastConfig.icon = 'check_circle';
    this.toastConfig.iconColor = '#2AC769';
    this.showNotification('bottom'); // Posición específica para éxito
  }

  private handleError(error: any, isUpdate: boolean) {
    this.toastConfig.content = isUpdate
      ? 'Error al actualizar el contacto'
      : 'Error al crear el contacto';
    //this.toastConfig.msg = error.error.message;
    this.toastConfig.msg = '';
    this.toastConfig.icon = 'dangerous';
    this.toastConfig.iconColor = '#CB2A2F';

    // Usa la posición por defecto (bottomRight) para errores
    this.showNotification();

    console.error(error);
    return throwError(() => error); // Propaga el error
  }

  /**
   * Loads existing documents for a partner when editing
   * @param partnerId ID of the partner to load documents for
   */
  loadPartnerDocuments(partnerId: number): void {
    // Check if the partner entity already has documents
    if (
      this.nzData.editingElement?.documents &&
      this.nzData.editingElement.documents.length > 0
    ) {
      // Map the existing documents to our uploadedDocuments format
      const existingDocs = this.nzData.editingElement.documents.map(
        (doc: any, index: number) => ({
          id: doc.id,
          name: doc.name,
          size: this.formatFileSize(
            typeof doc.fileSize === 'string' ? parseInt(doc.fileSize) : 0,
          ),
          documentTypeId: doc.documentTypeId,
          type: doc.type || 'unknown',
          progress: 100, // Already uploaded
          index: this.uploadedDocuments.length + index,
          isExisting: true, // Mark as existing document
          createdAt: doc.createdAt || new Date().toISOString(),
          expirationDate: doc.expirationDate || new Date().toISOString(),
          file: null, // No file object for existing documents
        }),
      );

      // Add to our documents array
      this.uploadedDocuments.push(...existingDocs);

      // Add to form array
      existingDocs.forEach((doc) => {
        this.getDocumentsFormArray().push(
          this.fb.group({
            documentTypeId: [doc.documentTypeId],
            name: [doc.name],
            file: [null], // No file for existing docs
            id: [doc.id],
            expirationDate: [doc.expirationDate],
          }),
        );
      });

      this.cdRef.detectChanges();
    } else {
      // If there are no documents in the partner entity, log it and continue
      console.log(
        'No documents found in partner entity, skipping document loading',
      );

      // We don't need to make an additional API call since we've already loaded
      // the partner and should have access to all its data

      /* Note: If you want to add a fallback API call in the future, you'll need to:
         1. Check what document-fetching method is available in your DocumentService
         2. Use the appropriate method with the correct parameters

         For example:

         this.documentService.getDocuments({
           filter: { partnerId: partnerId },
           startRow: 0,
           endRow: 100
         }).subscribe({...});
      */
    }
  }

  /**
   * Unlinks a document from a partner (used when removing existing documents)
   * @param documentId ID of the document to unlink
   * @param partnerId ID of the partner to unlink from
   */
  async unlinkDocument(documentId: number, partnerId: number): Promise<void> {
    try {
      await firstValueFrom(
        this.createDocumentLink(documentId, partnerId, false),
      );
      console.log('Document unlinked successfully');
    } catch (error) {
      console.error('Error unlinking document:', error);
      throw error;
    }
  }

  /**
   * Deletes a document (used after unlinking)
   * @param documentId ID of the document to delete
   */
  async deleteDocument(document: Document): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.documentService.deleteDocument([document.id]),
      );
      if (response && response.success) {
        document.isDeleted = true;
        document.fileExtension = 'any';
        document.partnerId = this.nzData.editingElement!.id;
        this.updateEvent.emit(document);
        this.savingPartner = false;
        console.log('Document deleted successfully');
        this.close();
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      throw error;
    }
  }

  handleUploadChange(info: NzUploadChangeParam): void {
    const file: NzUploadFile | undefined =
      info.fileList[info.fileList.length - 1];
    if (file) {
      const control = this.partnerForm.get('avatar');
      if (control) {
        control.setValue(file.originFileObj as File);
        getBase64(info.file!.originFileObj!, (img: string) => {
          this.avatarUrl = img;
          this.cdRef.detectChanges();
        });
      }
    }
  }

  showNotification(placement: NzNotificationPlacement = 'bottomRight'): void {
    this.notRef = this.notificationService.create(
      'blank',
      '',
      this.notificationTmpl,
      { nzPlacement: placement },
    );
    this.ms.loading = false;

    this.ms.drawerRef$.value.close();
  }

  emitUpdateEvent(event$: any) {
    this.updateEvent.emit(event$);
  }

  /**
   * Sets up listeners for isCompany and employee control changes with mutual exclusion
   */
  setupIsCompanyListener(): void {
    // Wait for the form to be initialized
    if (!this.partnerForm) {
      return;
    }

    // Get both controls
    const isCompanyControl = this.partnerForm.get('isCompany');
    const employeeControl = this.partnerForm.get('employee');

    if (isCompanyControl) {
      // Apply initial state based on current value
      this.updateSurNameAndPositionState(isCompanyControl.value);

      // Subscribe to value changes
      isCompanyControl.valueChanges.subscribe((isCompany) => {
        if (isCompany) {
          // If isCompany becomes true, automatically set employee to false
          employeeControl?.setValue(false, { emitEvent: false });
        }
        this.updateSurNameAndPositionState(isCompany);
      });
    }

    if (employeeControl) {
      // Subscribe to employee changes
      employeeControl.valueChanges.subscribe((employee) => {
        if (employee) {
          // If employee becomes true, automatically set isCompany to false
          isCompanyControl?.setValue(false, { emitEvent: false });
        }
        // Note: employee doesn't affect field states like isCompany does
      });
    }
  }

  /**
   * Updates the enabled/disabled state of surName, position, and parentId controls
   * @param isCompany Whether the partner is a company
   */
  updateSurNameAndPositionState(isCompany: boolean): void {
    const surNameControl = this.partnerForm.get('surName');
    const positionControl = this.partnerForm.get('position');
    const vatControl = this.partnerForm.get('vat');
    const employeeControl = this.partnerForm.get('employee');
    const parentIdControl = this.partnerForm.get('parentId');
    const currencyIdControl = this.partnerForm.get('currencyId');

    if (surNameControl && positionControl && vatControl) {
      if (isCompany) {
        // If it's a company, disable surname, position, and parent company
        surNameControl.disable();
        positionControl.disable();
        employeeControl!.disable();
        parentIdControl!.disable(); // Disable parent company for companies
        vatControl.enable(); // Enable VAT for companies
        currencyIdControl?.enable(); // Enable currency for companies
      } else {
        // If it's not a company, enable surname, position, and parent company
        surNameControl.enable();
        positionControl.enable();
        employeeControl!.enable();
        parentIdControl!.enable(); // Enable parent company for contacts
        vatControl.disable(); // Disable VAT when not a company
        currencyIdControl?.disable(); // Disable currency when not a company
        currencyIdControl?.setValue(null); // Clear currency value for non-companies
      }
    }
  }

  existControlInForm(control: string): boolean {
    return !!this.partnerForm.get(control);
  }

  expirationDateChange(date: any, document: any) {
    document.expirationDate = date;
  }

  // Helper method to get option list from columnsData by field name
  getOptionList(fieldName: string): any[] {
    const column = this.nzData.columnsData.find(
      (c) => c.pathData?.name === fieldName,
    );
    return column?.optionList || [];
  }

  ngOnDestroy(): void {
    // Remover listener nativo si fue registrado
    this.drawerEscapeBlocker.unblock();
  }

  protected readonly getPartnerOptions = getPartnerOptions;
}
